import jsPDF from 'jspdf'
import 'jspdf-autotable'
import moment from 'moment'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { currentDateTime } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtRoundNumberToSpecifedDigits } from 'src/helpers/commons/utils'

const user = wtGetFromLocalStorage('authUser')
const totalPagesExp = '{total_pages_count_string}'
const logo = 'assets/wil-icon.png'

const isOfficerEmployee = (employee) => Number(employee?.designation?.is_officer) === 1

const formatValue = (value) => wtRoundNumberToSpecifedDigits(Number(value || 0))

const getReportMonthLabel = (month, year) => {
  if (!month || !year) {
    return 'ATTENDANCE ANALYSIS REPORT'
  }

  return `FOR ${moment(`${year}-${String(month).padStart(2, '0')}-01`).format('MMMM YYYY').toUpperCase()}`
}

const getSectionRows = (rows = []) =>
  rows.map((employee, index) => [
    index + 1,
    { content: employee.id || '', styles: { textColor: 'black' } },
    {
      content: employee.full_name || '',
      styles: { textColor: '#0000FF', halign: 'left' },
    },
    {
      content: employee.designation?.name || '',
      styles: { textColor: '#0000FF', halign: 'left' },
    },
    {
      content: formatValue(employee.total_shift_hours),
      styles: { textColor: 'blue' },
    },
    {
      content: employee.on_leave ?? 0,
      styles: { textColor: 'blue' },
    },
    {
      content: employee.on_move ?? 0,
      styles: { textColor: 'blue' },
    },
    {
      content: employee.total_absent_unpresented ?? 0,
      styles: { textColor: Number(employee.total_absent_unpresented || 0) > 0 ? 'red' : 'blue' },
    },
    {
      content: formatValue(employee.total_hours),
      styles: { textColor: 'blue' },
    },
    {
      content: formatValue(employee.total_working_hours),
      styles: { textColor: 'blue' },
    },
    {
      content: formatValue(employee.total_difference),
      styles: { textColor: Number(employee.total_difference || 0) < 0 ? 'red' : 'blue' },
    },
    {
      content: formatValue(employee.total_difference_last_month),
      styles: {
        textColor: Number(employee.total_difference_last_month || 0) < 0 ? 'red' : 'blue',
      },
    },
  ])

const Pdf = (reportData = [], filters = {}) => {
  if (!Array.isArray(reportData) || reportData.length === 0) {
    lzNotifyErrorMessageFromResult('No Data found!')
    return
  }

  const officers = reportData.filter((employee) => isOfficerEmployee(employee))
  const nonOfficers = reportData.filter((employee) => !isOfficerEmployee(employee))
  const sections = [
    { title: 'OFFICERS', rows: officers },
    { title: 'NON-OFFICERS', rows: nonOfficers },
  ].filter((section) => section.rows.length > 0)

  if (sections.length === 0) {
    lzNotifyErrorMessageFromResult('No Data found!')
    return
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  })

  const header = (title) => {
    const pageSize = doc.internal.pageSize
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

    doc.setFontSize(11)
    doc.setTextColor('#3c4b64')
    doc.setFont('Helvetica', 'bold')
    doc.addImage(logo, 'PNG', 5, 4, 25, 25)
    doc.line(32, 6, 32, 25)

    doc.text('WAH', 35, 10, { align: 'left' })
    doc.text('INDUSTRIES', 35, 15)
    doc.text('LIMITED', 35, 20)
    doc.setFontSize(9)
    doc.text('www.wahindustries.com', 35, 25)
    doc.setFontSize(11)
    doc.setFont('Helvetica', 'bold')
    doc.text(title, pageWidth - 10, 14, { align: 'right' })
    doc.setTextColor('#083b8a')
    doc.setFont('Helvetica', 'bold')
    doc.text(getReportMonthLabel(filters.month, filters.year), pageWidth - 10, 22, {
      align: 'right',
    })
    doc.setTextColor('black')

    doc.line(10, 33, pageWidth - 10, 33)
  }

  const footer = () => {
    const pageNo = doc.internal.getNumberOfPages()
    const pageSize = doc.internal.pageSize
    const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight()
    const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth()

    doc.setFontSize(10)
    doc.setTextColor('black')
    doc.setFont('Helvetica', 'normal')
    doc.text('This report is printed through ERP at ', pageWidth / 2 - 45, pageHeight - 10)

    doc.setTextColor('#0202f7')
    doc.setFont('Helvetica', 'bold')
    doc.text(currentDateTime(), pageWidth / 2 + 19, pageHeight - 10)

    let str = 'Page ' + pageNo
    if (typeof doc.putTotalPages === 'function') {
      str = str + ' of ' + totalPagesExp
    }

    doc.setTextColor('black')
    doc.setFontSize(9)
    doc.line(14, pageHeight - 14, pageWidth - 14, pageHeight - 14)
    doc.text(str, pageWidth - 10, pageHeight - 9, { align: 'right' })
    doc.text('Printed By ', 20, pageHeight - 9, { align: 'left' })
    doc.setTextColor('blue')
    doc.text(`${user ? user.name : ''}`, 37, pageHeight - 9, { align: 'left' })
  }

  const headRows = [
    [
      { content: 'Sr' },
      { content: 'ID' },
      { content: 'Full Name' },
      { content: 'Designation' },
      { content: 'Total Shift Hours' },
      { content: 'On Leave (Days)' },
      { content: 'On Move (Days)' },
      { content: 'Absent Only' },
      { content: 'Total Hours' },
      { content: 'Working Hours' },
      { content: 'Diff this Month (hrs)' },
      { content: 'Difference Last Month (hrs)' },
    ],
  ]

  sections.forEach((section, index) => {
    if (index > 0) {
      doc.addPage()
    }

    doc.autoTable({
      head: headRows,
      body: getSectionRows(section.rows),
      startY: 40,
      margin: { top: 38, bottom: 20, left: 8, right: 8 },
      theme: 'grid',
      tableLineColor: [90, 105, 125],
      tableLineWidth: 0.35,
      tableWidth: 'auto',
      styles: {
        cellPadding: 1.4,
        lineColor: [90, 105, 125],
        lineWidth: {
          top: 0.35,
          right: 0.35,
          bottom: 0.35,
          left: 0.35,
        },
        halign: 'center',
        fontSize: 8.5,
        textColor: [0, 0, 0],
        overflow: 'linebreak',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [60, 75, 100],
        fontStyle: 'bold',
        fontSize: 9,
        lineColor: [60, 75, 100],
        lineWidth: {
          top: 0.35,
          right: 0.35,
          bottom: 0.35,
          left: 0.35,
        },
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 16 },
        2: { cellWidth: 42 },
        3: { cellWidth: 45 },
        4: { cellWidth: 18 },
        5: { cellWidth: 18 },
        6: { cellWidth: 18 },
        7: { cellWidth: 18 },
        8: { cellWidth: 18 },
        9: { cellWidth: 20 },
        10: { cellWidth: 26 },
        11: { cellWidth: 32 },
      },
      didDrawPage: () => {
        header(`ATTENDANCE ANALYSIS REPORT - ${section.title}`)
        footer()
      },
      didParseCell: (data) => {
        if (data.section === 'head') {
          data.cell.styles.lineColor = [60, 75, 100]
          data.cell.styles.lineWidth = {
            top: 0.35,
            right: 0.35,
            bottom: 0.35,
            left: 0.35,
          }
        }

        if (data.section === 'body') {
          data.cell.styles.lineColor = [90, 105, 125]
          data.cell.styles.lineWidth = {
            top: 0.35,
            right: 0.35,
            bottom: 0.35,
            left: 0.35,
          }
        }
      },
    })
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const pdfBlob = doc.output('blob')
  const pdfUrl = URL.createObjectURL(pdfBlob)
  window.open(pdfUrl, '_blank')
}

export default Pdf
